import { JSX, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import { asTitle } from "utils/Utils";

const buttonStyle = {
	position: "absolute",
	top: "calc(50% - 20px)",
	backgroundColor: "hsla(0, 0%, 96%, 0.3)",
	":hover": {
		backgroundColor: "hsla(0, 0%, 96%, 0.6)"
	}
};

export type ImageSlideshowProps = {
	images: Array<string>;
	index?: number;

	onClose: () => void;
};

export default function ImageSlideshow(
	props: Readonly<ImageSlideshowProps>
): JSX.Element {
	const { t } = useTranslation();

	const [currentIndex, setCurrentIndex] = useState(props.index ?? 0);

	const next = useCallback(() => {
		setCurrentIndex(index => index + 1);
	}, []);

	const previous = useCallback(() => {
		setCurrentIndex(index => index - 1);
	}, []);

	if (currentIndex >= props.images.length || currentIndex < 0) {
		return <Dialog open={true} onClose={props.onClose} />;
	}

	return (
		<Dialog open={true} onClose={props.onClose} maxWidth={"xl"}>
			{currentIndex > 0 && (
				<Tooltip
					sx={{
						...buttonStyle,
						left: 4
					}}
					title={asTitle(t("previous"))}>
					<IconButton aria-label="previous" onClick={previous}>
						<NavigateBeforeIcon />
					</IconButton>
				</Tooltip>
			)}
			<img
				aria-label="image"
				style={{
					maxWidth: "100%",
					maxHeight: "calc(100vh - 64px)",
				}}
				src={props.images[currentIndex]}
			/>
			{currentIndex < props.images.length - 1 && (
				<Tooltip
					sx={{
						...buttonStyle,
						right: 4
					}}
					title={asTitle(t("next"))}>
					<IconButton aria-label="next" onClick={next}>
						<NavigateNextIcon />
					</IconButton>
				</Tooltip>
			)}
		</Dialog>
	);
}
